import React, { useState } from 'react';
import { subscriptionService } from '../services/subscriptionService';
import { useNotification } from '../hooks/useNotification';
import Button from '../components/common/Button';

const SelectPackagePage = () => {
  const { showError } = useNotification();
  const [selectedPackage, setSelectedPackage] = useState('base');
  const [loading, setLoading] = useState(false);

  const packages = [
    {
      id: 'base',
      name: 'Base Package',
      price: '5',
      features: [
        'Unlimited debtor tracking',
        'In-app threshold alerts',
        'Dashboard analytics',
        'Search & filter',
        'Transaction history'
      ]
    },
    {
      id: 'basic_plus',
      name: 'Basic+ Package',
      price: '10',
      popular: true,
      features: [
        'All Base features',
        'PDF export to email',
        'Priority support',
        'Future: SMS/WhatsApp notifications'
      ]
    }
  ];

  const handleContinue = async () => {
    setLoading(true);
    try {
      const response = await subscriptionService.selectPackage(selectedPackage);

      // Redirect to payment URL
      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      }
    } catch (error) {
      showError(error.message || 'Failed to proceed to payment');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Package</h1>
          <p className="text-gray-600">Select the plan that works best for your business</p>
        </div>

        {/* Package Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg.id)}
              className={`relative bg-white rounded-lg shadow-lg p-8 cursor-pointer transition-all ${
                selectedPackage === pkg.id
                  ? 'ring-4 ring-primary-500 transform scale-105'
                  : 'hover:shadow-xl'
              }`}
            >
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-primary-600 text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold">
                  Popular
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-primary-600">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">BHD/month</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                  selectedPackage === pkg.id
                    ? 'border-primary-600 bg-primary-600'
                    : 'border-gray-300'
                }`}>
                  {selectedPackage === pkg.id && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <Button
            onClick={handleContinue}
            loading={loading}
            size="lg"
            className="px-12"
          >
            Continue to Payment
          </Button>

          <p className="text-sm text-gray-600 mt-4">
            30-day money back guarantee
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectPackagePage;
